"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import Swal from "sweetalert2"
import { UploadCloud, Trash2 } from "lucide-react"

const ReleaseModal = ({ isOpen, onClose, idLoadTransport, loadItems, selectedLoadItemId }) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [selectedLoadItem, setSelectedLoadItem] = useState(null)
  const [loadItemId, setLoadItemId] = useState(selectedLoadItemId || "")

  const handleFileSelect = (event) => {
    const filesWithPreview = Array.from(event.target.files).map((file) => ({
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
    }))

    setSelectedFiles((currentFiles) => [...currentFiles, ...filesWithPreview])
  }

  useEffect(() => {
    if (loadItems.length === 1) {
      setSelectedLoadItem(loadItems[0])
      setLoadItemId(loadItems[0].id.toString())
    }
  }, [loadItems])

  const handleSendFiles = async () => {
    let errored = false

    if (!selectedLoadItem) {
      Swal.fire({
        title: "Error!",
        text: "Please select a vehicle before sending files.",
        icon: "error",
        confirmButtonText: "Ok",
      })
      return
    }

    for (const { file } of selectedFiles) {
      if (file.type === "application/pdf") {
        const formData = new FormData()
        formData.append("document", file)
        formData.append("make", selectedLoadItem.make)
        formData.append("model", selectedLoadItem.model)
        formData.append("year", selectedLoadItem.year)
        const userToken = localStorage.getItem("userToken")
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_PREFIX}/loaditem/release/${selectedLoadItem.id}`,
            formData,
            {
              headers: {
                "Screen-Name": "/Load",
                Authorization: `Bearer ${userToken}`,
                "Content-Type": "multipart/form-data",
              },
            },
          )

          console.log(`${file.name} sent successfully:`, response.data)
        } catch (error) {
          console.error(`Error adding the Release PDF ${file.name}:`, error)
          errored = true
          Swal.fire({
            title: "Error!",
            text: `Failed to send ${file.name}.`,
            icon: "error",
            confirmButtonText: "Ok",
          })
          break
        }
      } else {
        alert("Please upload only PDF files.")
        errored = true
        break
      }
    }

    if (!errored) {
      Swal.fire({
        title: "Success!",
        text: "All files were successfully uploaded!",
        icon: "success",
        confirmButtonText: "Ok",
      }).then(() => {
        onClose()
      })
    }

    selectedFiles.forEach((fileObj) => URL.revokeObjectURL(fileObj.preview))
    setSelectedFiles([])
  }

  const removeSelectedFile = (index) => {
    URL.revokeObjectURL(selectedFiles[index].preview)
    setSelectedFiles((currentFiles) => currentFiles.filter((_, i) => i !== index))
  }

  const handleChange = (e) => {
    const selectedItemId = e.target.value
    setLoadItemId(selectedItemId)
    const selectedItem = loadItems.find((item) => item.id.toString() === selectedItemId)
    setSelectedLoadItem(selectedItem)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-zinc-900 rounded-xl p-8 w-full max-w-md shadow-2xl text-white">
        <h2 className="text-2xl font-semibold mb-2">Upload Release</h2>
        <p className="text-sm text-zinc-400 mb-4">
          Upload the Release document to ensure proper record-keeping and verification. Accepted format: PDF only.
        </p>

        {loadItems.length > 1 && (
          <select
            value={loadItemId}
            onChange={handleChange}
            className="mb-4 w-full p-2 rounded bg-zinc-800 border border-zinc-600 text-white"
          >
            <option value="">Select a Vehicle</option>
            {loadItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.make} {item.model} - {item.year}
              </option>
            ))}
          </select>
        )}

        <label
          htmlFor="dropzone-release"
          className="flex flex-col items-center justify-center w-full h-52 border-2 border-dashed border-zinc-600 rounded-lg cursor-pointer hover:bg-zinc-800 transition-colors"
        >
          <div className="flex flex-col items-center justify-center gap-2">
            <UploadCloud className="w-8 h-8" />
            <span className="text-sm text-zinc-400">Drop release file here</span>
            <span className="text-xs text-zinc-600">Click to upload or drag and drop your document here (Max 4MB)</span>
          </div>
          <input
            id="dropzone-release"
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept="application/pdf"
          />
        </label>

        <div className="mt-4 grid gap-4">
          {selectedFiles.map((fileObj, index) => (
            <div key={index} className="relative flex items-center justify-between bg-zinc-800 p-3 rounded-lg">
              <span className="text-sm truncate max-w-xs">{fileObj.name}</span>
              <button
                onClick={() => removeSelectedFile(index)}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-left space-x-3">
          <button
            onClick={handleSendFiles}
            className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-6 rounded-lg"
          >
            Send
          </button>
          <button
            onClick={onClose}
            className="bg-zinc-700 border border-zinc-500 text-white hover:bg-zinc-600 py-2 px-6 rounded-lg"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReleaseModal
